import { logger } from '@/utils/logger'

const log = logger.module('QAHealth')

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface ConsoleError {
  message: string
  timestamp: string
  stack?: string
  file?: string
  line?: number
  column?: number
  componentStack?: string
  suggestion?: string
}

export interface ErrorBoundaryLog {
  error?: { message?: string; name?: string; stack?: string }
  errorInfo?: { componentStack?: string }
  timestamp?: string
}

export interface QAIssue {
  id: string
  severity: 'critical' | 'warning' | 'info'
  title: string
  description?: string
  recommendation?: string
  category?: string
  sourceInfo?: Record<string, any>
  element?: string
  errorDetails?: any[]
  details?: Record<string, any>
}

export interface QAHealthCheckResult {
  category: string
  status: 'critical' | 'warning' | 'healthy'
  issues: QAIssue[]
  metrics: Record<string, any>
  timestamp: string
}

export interface QARun {
  type: string
  passed: boolean
  timestamp: string
  issuesFound?: number
  score?: number | null
  duration?: number | null
}

export interface QASummary {
  status: 'passing' | 'failing' | 'unknown'
  lastRun: string | null
  passRate: number | null
  needsRun: boolean
  recentIssues?: number
}

const QA_HISTORY_KEY = 'blissQAHistory'
const MAX_HISTORY_RECORDS = 50
const CONSOLE_ERROR_LOG_KEY = 'consoleErrorLog'
const ERROR_BOUNDARY_LOG_KEY = 'errorBoundaryLogs'

/**
 * Run browser-based QA checks
 */
export function runQAHealthCheck(): QAHealthCheckResult {
  const issues: QAIssue[] = []

  // Check for console errors with full details
  issues.push(...checkConsoleErrors())

  // Check for React errors in ErrorBoundary
  issues.push(...checkErrorBoundaryLogs())

  // Check for undefined/missing components
  issues.push(...checkComponentDefinitions())

  // Check build artifacts
  issues.push(...checkBuildStatus())

  // Check for common code smells with locations
  issues.push(...checkCodeSmells())

  const status = calculateQAStatus(issues)

  return {
    category: 'code-quality',
    status,
    issues,
    metrics: {
      componentsScanned: scanReactComponentTree(),
      consoleErrorCount: getConsoleErrorCount(),
      lastBuildStatus: getLastBuildStatus(),
      qaHistoryCount: getQAHistory().length
    },
    timestamp: new Date().toISOString()
  }
}

/**
 * Check for console errors with detailed information
 */
function checkConsoleErrors(): QAIssue[] {
  const issues: QAIssue[] = []
  const errorLog = getConsoleErrorLog()
  const errorCount = errorLog.length

  if (errorCount > 0) {
    // Group errors by type/message for better organization
    const errorGroups = groupErrorsByMessage(errorLog)

    issues.push({
      id: 'console-errors',
      severity: errorCount > 5 ? 'critical' : 'warning',
      title: `${errorCount} Console Error${errorCount > 1 ? 's' : ''}`,
      description: 'JavaScript errors detected in browser console',
      recommendation: 'Open browser DevTools (F12) and fix console errors',
      category: 'code-quality',
      sourceInfo: {
        type: 'console',
        errorCount,
        errors: errorLog.slice(0, 10),
        groupedErrors: errorGroups
      }
    })

    // Add individual error issues for critical ones
    const criticalErrors = errorLog.filter((e: ConsoleError) =>
      e.message?.includes('undefined') ||
      e.message?.includes('is not a function') ||
      e.message?.includes('Cannot read') ||
      e.message?.includes('Failed to fetch')
    )

    criticalErrors.slice(0, 3).forEach((error: ConsoleError, idx: number) => {
      issues.push({
        id: `console-error-${idx}`,
        severity: 'critical',
        title: truncateMessage(error.message, 60),
        description: error.message,
        recommendation: error.suggestion || 'Check the stack trace and fix the root cause',
        category: 'code-quality',
        sourceInfo: {
          type: 'console-detail',
          message: error.message,
          stack: error.stack,
          file: error.file,
          line: error.line,
          column: error.column,
          timestamp: error.timestamp,
          componentStack: error.componentStack
        }
      })
    })
  }

  return issues
}

/**
 * Check ErrorBoundary logs with detailed component information
 */
function checkErrorBoundaryLogs(): QAIssue[] {
  const issues: QAIssue[] = []

  try {
    const errorLogs = JSON.parse(sessionStorage.getItem(ERROR_BOUNDARY_LOG_KEY) || '[]')
    if (errorLogs.length > 0) {
      const recentErrors = errorLogs.filter((log: ErrorBoundaryLog) => {
        const logTime = new Date(log.timestamp || Date.now()).getTime()
        const now = Date.now()
        return (now - logTime) < 3600000
      })

      if (recentErrors.length > 0) {
        // Extract detailed component info from each error
        const errorDetails = recentErrors.map((log: ErrorBoundaryLog) => {
          const componentStack = log.errorInfo?.componentStack || ''
          const components = parseComponentStack(componentStack)

          return {
            message: log.error?.message || 'Unknown error',
            name: log.error?.name || 'Error',
            stack: log.error?.stack,
            component: components[0] || 'Unknown',
            componentPath: components.slice(0, 5),
            file: extractFileFromStack(log.error?.stack),
            timestamp: log.timestamp
          }
        })

        issues.push({
          id: 'error-boundary-catches',
          severity: 'critical',
          title: `ErrorBoundary Caught ${recentErrors.length} Error(s)`,
          description: 'Recent component crashes prevented by ErrorBoundary',
          recommendation: 'Review error logs and fix root causes',
          category: 'code-quality',
          sourceInfo: {
            type: 'error-boundary',
            errorCount: recentErrors.length,
            errors: errorDetails
          },
          errorDetails
        })

        // Add individual issues for each unique error
        const uniqueErrors = getUniqueErrors(errorDetails)
        uniqueErrors.slice(0, 3).forEach((error, idx) => {
          issues.push({
            id: `error-boundary-${idx}`,
            severity: 'critical',
            title: `Crash in ${error.component}`,
            description: error.message,
            recommendation: `Fix the error in ${error.component}. ${getSuggestionForError(error.message)}`,
            category: 'code-quality',
            sourceInfo: {
              type: 'component-crash',
              component: error.component,
              componentPath: error.componentPath,
              file: error.file,
              stack: error.stack,
              message: error.message
            }
          })
        })
      }
    }
  } catch (e) {
    // Silently fail
  }

  return issues
}

/**
 * Check for undefined component references
 */
function checkComponentDefinitions(): QAIssue[] {
  const issues: QAIssue[] = []

  // Check for React error boundaries catching undefined components
  const reactErrors = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__?.renderers
  if (reactErrors) {
    try {
      const errorCount = Object.keys(reactErrors).length
      if (errorCount > 0) {
        issues.push({
          id: 'react-errors-detected',
          severity: 'warning',
          title: 'React Errors Detected',
          description: `React DevTools reports ${errorCount} potential issues`,
          recommendation: 'Check React DevTools console for details',
          category: 'code-quality'
        })
      }
    } catch (e) {
      // Silently fail if DevTools not available
    }
  }

  // Check for elements with error classes
  const errorElements = document.querySelectorAll('[class*="error"], [class*="Error"]')
  if (errorElements.length > 3) {
    const locations = Array.from(errorElements).slice(0, 5).map((el: any) => ({
      selector: generateSelector(el),
      className: el.className,
      text: el.textContent?.slice(0, 50)
    }))

    issues.push({
      id: 'multiple-error-states',
      severity: 'info',
      title: 'Multiple Error States Visible',
      description: `Found ${errorElements.length} elements with error classes`,
      recommendation: 'Verify these are intentional error messages, not component failures',
      category: 'code-quality',
      sourceInfo: {
        type: 'dom-elements',
        count: errorElements.length,
        samples: locations
      }
    })
  }

  return issues
}

/**
 * Check build status from recent builds
 */
function checkBuildStatus(): QAIssue[] {
  const issues: QAIssue[] = []
  const lastBuild = getLastBuildStatus()
  const buildDetails = getLastBuildDetails()

  if (lastBuild === 'unknown') {
    issues.push({
      id: 'build-status-unknown',
      severity: 'info',
      title: 'Build Status Unknown',
      description: 'No recent build information available',
      recommendation: 'Run `npm run build` to verify build status',
      category: 'code-quality'
    })
  } else if (lastBuild === 'failed') {
    issues.push({
      id: 'build-failed',
      severity: 'critical',
      title: 'Last Build Failed',
      description: buildDetails?.error || 'Production build is currently failing',
      recommendation: 'Run `npm run build` and fix errors immediately',
      category: 'code-quality',
      sourceInfo: {
        type: 'build',
        status: 'failed',
        error: buildDetails?.error,
        file: buildDetails?.file,
        line: buildDetails?.line,
        timestamp: new Date().toISOString() // Build time tracked via console
      }
    })
  }

  return issues
}

/**
 * Check for common code smells with specific locations
 */
function checkCodeSmells(): QAIssue[] {
  const issues: QAIssue[] = []

  // Check for missing alt text on images
  const images = document.querySelectorAll('img:not([alt])')
  if (images.length > 5) {
    const locations = Array.from(images).slice(0, 5).map((img: any) => ({
      selector: generateSelector(img),
      src: img.src?.slice(-50),
      parent: img.parentElement?.className
    }))

    issues.push({
      id: 'missing-alt-text',
      severity: 'info',
      title: 'Missing Image Alt Text',
      description: `${images.length} images missing alt attributes (accessibility issue)`,
      recommendation: 'Add descriptive alt text to all images',
      category: 'code-quality',
      sourceInfo: {
        type: 'accessibility',
        count: images.length,
        samples: locations
      }
    })
  }

  // Check for inline styles (anti-pattern)
  const inlineStyleElements = document.querySelectorAll('[style]:not(svg):not(path)')
  if (inlineStyleElements.length > 50) {
    const locations = Array.from(inlineStyleElements).slice(0, 5).map((el: any) => ({
      selector: generateSelector(el),
      style: el.getAttribute('style')?.slice(0, 100),
      tag: el.tagName.toLowerCase()
    }))

    issues.push({
      id: 'excessive-inline-styles',
      severity: 'info',
      title: 'Excessive Inline Styles',
      description: `${inlineStyleElements.length} elements with inline styles`,
      recommendation: 'Move styles to CSS classes for maintainability',
      category: 'code-quality',
      sourceInfo: {
        type: 'code-smell',
        count: inlineStyleElements.length,
        samples: locations
      }
    })
  }

  // Check for deprecated HTML attributes
  const deprecatedElements = document.querySelectorAll('[align], [bgcolor], [border]:not(table)')
  if (deprecatedElements.length > 0) {
    const locations = Array.from(deprecatedElements).slice(0, 5).map((el: any) => ({
      selector: generateSelector(el),
      attributes: getDeprecatedAttributes(el),
      tag: el.tagName.toLowerCase()
    }))

    issues.push({
      id: 'deprecated-html',
      severity: 'warning',
      title: 'Deprecated HTML Attributes',
      description: `${deprecatedElements.length} elements using deprecated attributes`,
      recommendation: 'Replace with modern CSS',
      category: 'code-quality',
      sourceInfo: {
        type: 'code-smell',
        count: deprecatedElements.length,
        samples: locations
      }
    })
  }

  return issues
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Generate a unique CSS selector for an element
 */
function generateSelector(element: Element): string {
  if (!element) return 'unknown'

  if (element.id) {
    return `#${element.id}`
  }

  if (element.className && typeof element.className === 'string') {
    const classes = element.className.split(' ').filter(c => c && !c.includes('--')).slice(0, 2)
    if (classes.length > 0) {
      return `${element.tagName.toLowerCase()}.${classes.join('.')}`
    }
  }

  const parent = element.parentElement
  if (parent) {
    const siblings = Array.from(parent.children).filter(c => c.tagName === element.tagName)
    const index = siblings.indexOf(element)
    if (siblings.length > 1) {
      return `${element.tagName.toLowerCase()}:nth-of-type(${index + 1})`
    }
  }

  return element.tagName.toLowerCase()
}

/**
 * Parse React component stack into array
 */
function parseComponentStack(stack: string): string[] {
  if (!stack) return []

  return stack
    .split('\n')
    .filter(line => line.trim().startsWith('at ') || line.trim().startsWith('in '))
    .map(line => {
      const match = line.match(/(?:at|in)\s+(\w+)/)
      return match ? match[1] : null
    })
    .filter((x: string | null): x is string => x !== null)
}

/**
 * Extract file and line from error stack
 */
function extractFileFromStack(stack: string): { path: string; line: number; column: number } | null {
  if (!stack) return null

  const lines = stack.split('\n')
  for (const line of lines) {
    const srcMatch = line.match(/(?:src\/[\w\-\/]+\.\w+):(\d+):(\d+)/)
    if (srcMatch) {
      return {
        path: srcMatch[0].split(':')[0],
        line: parseInt(srcMatch[1]),
        column: parseInt(srcMatch[2])
      }
    }

    const componentMatch = line.match(/\(([\w\-\/]+\.(?:jsx?|tsx?)):(\d+):(\d+)\)/)
    if (componentMatch) {
      return {
        path: componentMatch[1],
        line: parseInt(componentMatch[2]),
        column: parseInt(componentMatch[3])
      }
    }
  }

  return null
}

/**
 * Group errors by message for summary
 */
function groupErrorsByMessage(errors: ConsoleError[]): Record<string, any> {
  const groups: Record<string, any> = {}

  errors.forEach((error: ConsoleError) => {
    const key = error.message?.slice(0, 100) || 'Unknown'
    if (!groups[key]) {
      groups[key] = {
        message: error.message,
        count: 0,
        firstSeen: error.timestamp,
        lastSeen: error.timestamp,
        samples: []
      }
    }
    groups[key].count++
    groups[key].lastSeen = error.timestamp
    if (groups[key].samples.length < 3) {
      groups[key].samples.push(error)
    }
  })

  return Object.values(groups).sort((a: any, b: any) => b.count - a.count)
}

/**
 * Get unique errors by message
 */
function getUniqueErrors(errors: any[]): any[] {
  const seen = new Set()
  return errors.filter(error => {
    const key = error.message
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * Get suggestion for common error types
 */
function getSuggestionForError(message: string): string {
  if (!message) return ''

  if (message.includes('undefined') || message.includes('null')) {
    return 'Add null checks or optional chaining (?.) before accessing properties.'
  }
  if (message.includes('is not a function')) {
    return 'Check that the function is properly imported/defined before calling.'
  }
  if (message.includes('Cannot read')) {
    return 'The object is undefined. Add a null check or default value.'
  }
  if (message.includes('Failed to fetch')) {
    return 'Network request failed. Check API endpoint and error handling.'
  }
  if (message.includes('key')) {
    return 'Add unique key props to list items in map() calls.'
  }

  return ''
}

/**
 * Truncate message for display
 */
function truncateMessage(message: string, maxLength: number): string {
  if (!message) return 'Unknown Error'
  if (message.length <= maxLength) return message
  return message.slice(0, maxLength) + '...'
}

/**
 * Get deprecated HTML attributes from element
 */
function getDeprecatedAttributes(element: Element): string[] {
  const deprecated: string[] = []
  if (element.hasAttribute('align')) deprecated.push('align')
  if (element.hasAttribute('bgcolor')) deprecated.push('bgcolor')
  if (element.hasAttribute('border')) deprecated.push('border')
  return deprecated
}

/**
 * Calculate QA status from issues
 */
function calculateQAStatus(issues: QAIssue[]): 'critical' | 'warning' | 'healthy' {
  const critical = issues.filter(i => i.severity === 'critical').length
  const warnings = issues.filter(i => i.severity === 'warning').length

  if (critical > 0) return 'critical'
  if (warnings > 2) return 'warning'
  return 'healthy'
}

/**
 * Scan React component tree depth
 */
function scanReactComponentTree(): number | string {
  const reactElements = document.querySelectorAll('[data-reactroot], [data-reactid]')
  return reactElements.length || 'N/A'
}

/**
 * Get console error count from monitoring
 */
function getConsoleErrorCount(): number {
  return getConsoleErrorLog().length
}

/**
 * Get detailed console error log
 */
export function getConsoleErrorLog(): ConsoleError[] {
  try {
    return JSON.parse(sessionStorage.getItem(CONSOLE_ERROR_LOG_KEY) || '[]')
  } catch {
    return []
  }
}

/**
 * Get last build status - always success if app loaded
 */
function getLastBuildStatus(): string {
  // If this code is running, the build succeeded
  return 'success'
}

/**
 * Get last build details - app running means success
 */
function getLastBuildDetails(): any {
  return {
    version: '1.0.0',
    environment: import.meta.env?.MODE || 'production',
    timestamp: new Date().toISOString()
  }
}

/**
 * Record build status - console logging only
 */
export function recordBuildStatus(status: string, details: Record<string, any> = {}): void {
  // Build status tracked via console logging, not localStorage
  console.log('[QA] Build status:', status, details)
  log.info('Build status recorded', { status })
}

/**
 * Get QA run history
 */
export function getQAHistory(): QARun[] {
  // QA history is session-only - not persisted
  return []
}

/**
 * Record QA run - console logging only
 */
export function recordQARun(type: string, passed: boolean, details: Record<string, any> = {}): void {
  // QA runs tracked via console logging only
  console.log('[QA] Run recorded:', { type, passed, ...details })
  log.info('QA run recorded', { type, passed })
}

/**
 * Get QA summary for badges
 */
export function getQASummary(): QASummary {
  const history = getQAHistory()
  const lastRun = history[0]

  if (!lastRun) {
    return {
      status: 'unknown',
      lastRun: null,
      passRate: null,
      needsRun: true
    }
  }

  const recent = history.slice(0, 10)
  const passCount = recent.filter(r => r.passed).length
  const passRate = Math.round((passCount / recent.length) * 100)

  const lastRunTime = new Date(lastRun.timestamp).getTime()
  const needsRun = (Date.now() - lastRunTime) > (4 * 60 * 60 * 1000)

  return {
    status: lastRun.passed ? 'passing' : 'failing',
    lastRun: lastRun.timestamp,
    passRate,
    needsRun,
    recentIssues: lastRun.issuesFound || 0
  }
}

/**
 * Enhanced console error monitoring with full stack traces
 */
export function initializeConsoleMonitoring(): void {
  // Clear previous session errors
  sessionStorage.setItem(CONSOLE_ERROR_LOG_KEY, '[]')

  const originalError = console.error
  console.error = (...args: any[]) => {
    const message = args[0]?.toString() || ''
    if (message.includes('Download the React DevTools') ||
      message.includes('DevTools backend') ||
      message.includes('Extension context invalidated') ||
      message.includes('ResizeObserver loop')) {
      originalError.apply(console, args)
      return
    }

    const errorInfo: ConsoleError = {
      message: formatErrorMessage(args),
      timestamp: new Date().toISOString(),
      stack: undefined,
      file: undefined,
      line: undefined,
      column: undefined,
      componentStack: undefined
    }

    if (args[0] instanceof Error) {
      errorInfo.stack = args[0].stack
      const fileInfo = extractFileFromStack(args[0].stack || '')
      if (fileInfo) {
        errorInfo.file = fileInfo.path
        errorInfo.line = fileInfo.line
        errorInfo.column = fileInfo.column
      }
    } else {
      try {
        const stack = new Error().stack
        errorInfo.stack = stack
        const fileInfo = extractFileFromStack(stack || '')
        if (fileInfo) {
          errorInfo.file = fileInfo.path
          errorInfo.line = fileInfo.line
          errorInfo.column = fileInfo.column
        }
      } catch (e) {
        // Ignore
      }
    }

    for (const arg of args) {
      if (typeof arg === 'string' && arg.includes('at ') && arg.includes('\n')) {
        errorInfo.componentStack = arg
        break
      }
    }

    errorInfo.suggestion = getSuggestionForError(errorInfo.message)

    try {
      const errorLog = getConsoleErrorLog()
      errorLog.push(errorInfo)
      sessionStorage.setItem(CONSOLE_ERROR_LOG_KEY, JSON.stringify(errorLog.slice(-20)))
    } catch (e) {
      // Fail silently if sessionStorage is full
    }

    originalError.apply(console, args)
  }

  const originalWarn = console.warn
  console.warn = (...args: any[]) => {
    const message = args[0]?.toString() || ''
    if (message.includes('componentWillReceiveProps') ||
      message.includes('componentWillMount') ||
      message.includes('DevTools')) {
      originalWarn.apply(console, args)
      return
    }
    originalWarn.apply(console, args)
  }

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const errorInfo: ConsoleError = {
      message: `Unhandled Promise Rejection: ${event.reason?.message || event.reason}`,
      timestamp: new Date().toISOString(),
      stack: event.reason?.stack
    }

    const fileInfo = extractFileFromStack(event.reason?.stack)
    if (fileInfo) {
      errorInfo.file = fileInfo.path
      errorInfo.line = fileInfo.line
      errorInfo.column = fileInfo.column
    }

    errorInfo.suggestion = getSuggestionForError(errorInfo.message)

    try {
      const errorLog = getConsoleErrorLog()
      errorLog.push(errorInfo)
      sessionStorage.setItem(CONSOLE_ERROR_LOG_KEY, JSON.stringify(errorLog.slice(-20)))
    } catch (e) {
      // Fail silently
    }
  })

  log.info('Enhanced console monitoring initialized')
}

/**
 * Format error message from console.error args
 */
function formatErrorMessage(args: any[]): string {
  return args.map(arg => {
    if (arg instanceof Error) {
      return arg.message
    }
    if (typeof arg === 'object') {
      try {
        return JSON.stringify(arg).slice(0, 200)
      } catch {
        return String(arg)
      }
    }
    return String(arg)
  }).join(' ').slice(0, 500)
}

/**
 * Get code quality metrics dashboard
 */
export function getCodeQualityMetrics(): any {
  const history = getQAHistory()
  const qaCheck = runQAHealthCheck()
  const summary = getQASummary()

  return {
    currentStatus: qaCheck.status,
    currentIssues: qaCheck.issues.length,
    passRate: summary.passRate,
    lastRun: summary.lastRun,
    consoleErrors: getConsoleErrorCount(),
    buildStatus: getLastBuildStatus(),
    trendsUp: calculateTrend(history),
    metrics: qaCheck.metrics,
    errorDetails: getConsoleErrorLog().slice(0, 5)
  }
}

/**
 * Calculate quality trend
 */
function calculateTrend(history: QARun[]): boolean {
  if (history.length < 5) return false

  const recent = history.slice(0, 5)
  const older = history.slice(5, 10)

  const recentPass = recent.filter(r => r.passed).length / recent.length
  const olderPass = older.filter(r => r.passed).length / (older.length || 1)

  return recentPass > olderPass
}

/**
 * Quick QA status check (for dashboard badge)
 */
export function quickQACheck(): { status: string; message: string } {
  const consoleErrors = getConsoleErrorCount()
  const buildStatus = getLastBuildStatus()
  const summary = getQASummary()

  if (consoleErrors > 0 || buildStatus === 'failed') {
    return { status: 'critical', message: 'QA issues detected' }
  }

  if (summary.status === 'failing') {
    return { status: 'warning', message: 'Recent QA failures' }
  }

  if (summary.needsRun) {
    return { status: 'info', message: 'QA check due' }
  }

  return { status: 'healthy', message: 'All checks passing' }
}

/**
 * Clear all error logs (for testing/reset)
 */
export function clearErrorLogs(): void {
  sessionStorage.removeItem(CONSOLE_ERROR_LOG_KEY)
  sessionStorage.removeItem(ERROR_BOUNDARY_LOG_KEY)
  log.info('Error logs cleared')
}
